import { ElementType, Dimensions, ToolboxItem, ComponentField } from '../types';
import { DEFAULT_STOCKER_PROCESS_WAIT_SEC } from '../features/equipment/equipmentConstants';

/**
 * Grid configuration
 */
export const GRID_SIZE = 10;
/** Default world units per meter when no DXF floor plan is loaded (1 m = 6 units). */
export const DEFAULT_UNITS_PER_METER_WITHOUT_FLOORPLAN = 6;
export const UNIT_TO_MM = 11;
export const MM_PER_METER = 1000;
export const GRID_COLOR = '#e0e0e0';
export const GRID_COLOR_DARK = '#404040';
export const GRID_OPACITY = 0.3;

/**
 * Canvas configuration
 */
export const CANVAS_WIDTH = 5000;
export const CANVAS_HEIGHT = 5000;
export const MIN_ZOOM = 0.01;
export const MAX_ZOOM = 200;
export const ZOOM_STEP = 0.1;
/** Default canvas zoom with no DXF floor plan (700%). Floor-plan loads still fit-to-bounds. */
export const DEFAULT_ZOOM = 7;

/**
 * Selection configuration
 */
export const SELECTION_COLOR = '#0ea5e9';
export const SELECTION_STROKE_WIDTH = 1;
export const HOVER_COLOR = '#38bdf8';

/**
 * Snap configuration
 */
export const SNAP_THRESHOLD = 5; // pixels
export const ALIGNMENT_GUIDE_COLOR = '#ff00ff';
export const ALIGNMENT_GUIDE_WIDTH = 1;

/**
 * Point Draw connection discovery (visual only — does not change snap distance).
 * Radius is in screen pixels so discoverability feels identical at every zoom.
 */
export const POINT_DRAW_DISCOVER_RADIUS_PX = 96;

/**
 * When true, Circular Junctions render port numbers, port dots, connection
 * arrows, and direction arrows. Production editing keeps these off — the
 * underlying port/connection data is unchanged either way.
 */
export const DEBUG_CIRCLE_JUNCTION_OVERLAY = false;

/**
 * History configuration
 */
export const MAX_HISTORY_SIZE = 100;
export const AUTOSAVE_INTERVAL = 30000; // 30 seconds

/**
 * How long transient UI banners / notices stay visible before auto-dismiss.
 */
export const NOTICE_AUTO_DISMISS_MS = 5000;

/**
 * Performance configuration
 */
export const RENDER_BUFFER_SIZE = 100; // Number of elements to render at once
export const DEBOUNCE_DELAY = 300; // ms

/**
 * Default rail thickness for newly created tracks (toolbox, Point Draw, auto-router).
 * Stored on elements as customProperties.thicknessMeters; existing layouts keep
 * whatever value they already have.
 */
export const DEFAULT_TRACK_THICKNESS_METERS = 0.05;

/**
 * Default curve radius for newly created curve tracks (toolbox, Point Draw, auto-router).
 * Stored on elements as customProperties.radiusMeters; existing layouts keep
 * whatever value they already have.
 */
export const DEFAULT_CURVE_RADIUS_METERS = 0.2;

/** Default travel direction for newly created track segments. */
export const DEFAULT_TRACK_DIRECTION = 'uni' as const;
export type TrackDirection = 'bi' | 'uni';

/**
 * Default dimensions for each element class (used as a fallback; individual
 * SKUs in TOOLBOX_ITEMS may override these).
 */
export const DEFAULT_DIMENSIONS: Record<ElementType, Dimensions> = {
  'overhead-rail': { width: 15, height: 3.2 },
  'overhead-curve': { width: 64, height: 64 },
  'cross-rail': { width: 64, height: 64 },
  'oht-vehicle': { width: 4.5, height: 5 },
  stocker: { width: 10, height: 18 },
  stb: { width: 50, height: 20 },
  ohb: { width: 50, height: 18 },
  lifter: { width: 45, height: 45 },
  'support-pillar': { width: 4, height: 4 },
  'horizontal-beam': { width: 100, height: 3 },
  'suspension-rod': { width: 1, height: 1 },
  rectangle: { width: 100, height: 100 },
  circle: { width: 80, height: 80 },
  text: { width: 120, height: 18 },
};

/** Curve track bounding box in canvas units (radius stored in width/height). */
export const CURVE_RAIL_DIMENSIONS: Dimensions = { width: 5, height: 5 };

/**
 * BriteLab Component Library (F2.1)
 *
 * Pre-loaded catalog of AMHS hardware SKUs. Each item maps to an ElementType
 * class and carries parametric defaults that become the element's
 * customProperties when placed. Assets are rendered as parametric geometry
 * (not textures) so they can be color-coded dynamically.
 */
export const TOOLBOX_ITEMS: ToolboxItem[] = [
  {
    id: 'rail-at1',
    type: 'overhead-rail',
    sku: 'AeroTrak AT1',
    name: 'Track',
    icon: '─',
    description: 'Straight track segment',
    defaultDimensions: DEFAULT_DIMENSIONS['overhead-rail'],
    category: 'Track',
    defaultProperties: {
      railType: 'straight',
      direction: DEFAULT_TRACK_DIRECTION,
      heightM: 3.2,
      lengthMeters: 2,
      thicknessMeters: DEFAULT_TRACK_THICKNESS_METERS,
    },
  },
  {
    id: 'rail-at1-vertical',
    type: 'overhead-rail',
    sku: 'AeroTrak AT1-V',
    name: 'Vertical Track',
    icon: '│',
    description: 'Vertical straight track segment',
    defaultDimensions: DEFAULT_DIMENSIONS['overhead-rail'],
    defaultRotation: 90,
    category: 'Track',
    defaultProperties: {
      railType: 'straight',
      direction: DEFAULT_TRACK_DIRECTION,
      heightM: 3.2,
      lengthMeters: 3,
      thicknessMeters: DEFAULT_TRACK_THICKNESS_METERS,
    },
  },
  {
    id: 'rail-at2',
    type: 'overhead-curve',
    sku: 'AeroTrak AT2',
    name: '90° Curve',
    icon: '⌜',
    description: '90 degree curved track segment',
    defaultDimensions: DEFAULT_DIMENSIONS['overhead-curve'],
    category: 'Track',
    defaultProperties: {
      railType: 'curve90',
      direction: DEFAULT_TRACK_DIRECTION,
      thicknessMeters: DEFAULT_TRACK_THICKNESS_METERS,
      radiusMeters: DEFAULT_CURVE_RADIUS_METERS,
    },
  },
  {
    id: 'rail-at3',
    type: 'overhead-curve',
    sku: 'AeroTrak AT3',
    name: '45° Curve',
    icon: '◠',
    description: '45 degree curved track segment',
    defaultDimensions: DEFAULT_DIMENSIONS['overhead-curve'],
    category: 'Track',
    defaultProperties: {
      railType: 'curve45',
      direction: DEFAULT_TRACK_DIRECTION,
      thicknessMeters: DEFAULT_TRACK_THICKNESS_METERS,
      radiusMeters: DEFAULT_CURVE_RADIUS_METERS,
    },
  },
  {
    id: 'rail-at4',
    type: 'overhead-curve',
    sku: 'AeroTrak AT4',
    name: '180° Curve',
    icon: '∩',
    description: '180 degree curved track segment',
    defaultDimensions: DEFAULT_DIMENSIONS['overhead-curve'],
    category: 'Track',
    defaultProperties: {
      railType: 'curve180',
      direction: DEFAULT_TRACK_DIRECTION,
      thicknessMeters: DEFAULT_TRACK_THICKNESS_METERS,
      radiusMeters: DEFAULT_CURVE_RADIUS_METERS,
    },
  },
  {
    id: 'rail-circle-junction',
    type: 'cross-rail',
    sku: 'AeroTrak CJ1',
    name: 'Circle Junction',
    icon: '◎',
    description: 'Rotating turntable junction — hub rail spins to align with exit ports',
    // Footprint used by toolbox drop and on-rail insert (edit these to change size).
    defaultDimensions: {
      width: 7,
      height: 7,
    },
    category: 'Track', 
    defaultProperties: {
      railType: 'junction-circle',
      direction: DEFAULT_TRACK_DIRECTION,
      heightM: 3.2,
      thicknessMeters: DEFAULT_TRACK_THICKNESS_METERS,
      // merge: prefer straight-through at the hub; lane changes use H-connectors.
      mode: 'merge',
      turntableAngleDeg: 0,
      /** When true, hub disk rotates as OHTs approach / transfer. */
      animateTurntable: true,
      /** Dedicated OHT wait before the turntable animation (seconds, sim time at 1×). */
      junctionWaitSec: 3,
      /** Full 8-way hub (N/NE/E/SE/S/SW/W/NW); unused spokes stay visually hidden. */
      maxPorts: 8,
      junctionInputs: 2,
      junctionOutputs: 2,
      radiusMeters: DEFAULT_CURVE_RADIUS_METERS,
    },
  },
  {
    id: 'rail-connector',
    type: 'overhead-rail',
    sku: 'AeroTrak CN1',
    name: 'Connector',
    icon: 'Ｈ',
    description:
      'H-shaped connector between two parallel tracks (one solid leg, dotted bridge + other leg)',
    // Vertical by default (matches parallel vertical track layouts).
    defaultRotation: 90,
    defaultDimensions: {
      width: 6,
      height: 12,
    },
    category: 'Track',
    defaultProperties: {
      railType: 'connector',
      direction: 'bi',
      heightM: 3.2,
      lengthMeters: 1,
      thicknessMeters: DEFAULT_TRACK_THICKNESS_METERS,
      /** Lateral span (m) between the two parallel rails the H bridges. */
      shiftOffsetMeters: 2,
      /** home = solid on home leg; shifted = solid on far leg. */
      shiftState: 'home',
    },
  },
  {
    id: 'oht',
    type: 'oht-vehicle',
    sku: 'OHT',
    name: 'OHT',
    icon: '▮',
    description: 'Overhead hoist transport vehicle',
    defaultDimensions: DEFAULT_DIMENSIONS['oht-vehicle'],
    category: 'OHT',
    defaultProperties: {
      ohtMode: 'dedicated',
      ohtRole: 'group',
      toolType: 'PHOTO',
      cruiseSpeedOverride: 2.5,
    },
  },
  {
    id: 'stb',
    type: 'stb',
    sku: 'Side Track Buffer',
    name: 'STB',
    icon: '▤',
    description: 'Side track buffer station',
    defaultDimensions: DEFAULT_DIMENSIONS.stb,
    category: 'Storage',
    defaultProperties: { slots: 4 },
  },
  {
    id: 'ohb',
    type: 'ohb',
    sku: 'Overhead Hoist Buffer',
    name: 'OHB',
    icon: '▥',
    description: 'Overhead hoist buffer',
    defaultDimensions: DEFAULT_DIMENSIONS.ohb,
    category: 'Storage',
    defaultProperties: { slots: 4, mount: 'inline' },
  },
  {
    id: 'stocker',
    type: 'stocker',
    sku: 'Stocker',
    name: 'Stocker',
    icon: '■',
    description: 'Small stocker footprint',
    defaultDimensions: DEFAULT_DIMENSIONS.stocker,
    category: 'Stocker',
    defaultProperties: {
      waferSize: '300mm',
      bayCount: '4',
      showLabel: true,
      processWaitSec: DEFAULT_STOCKER_PROCESS_WAIT_SEC,
      // No default process type — untyped stockers omit processType in /amhs/run
      // (materialFlow passThrough). User picks a type for bay process stockers.
      processTypes: [],
      processType: null,
    },
  },
  {
    id: 'vertical-lifter',
    type: 'lifter',
    sku: 'Vertical Lifter',
    name: 'Vertical Lifter',
    icon: '⇕',
    description: 'Continuous vertical elevator lifter with infeed conveyor and multi-tier carriage',
    defaultDimensions: { width: 14, height: 18 },
    category: 'Equipment',
    defaultProperties: {
      objectType: 'equipment',
      equipmentModel: 'Vertical Lifter',
      equipmentType: 'LIFTER',
      source: 'toolbox',
    },
  },
  {
    id: 'process-tool',
    type: 'rectangle',
    sku: 'Process Tool',
    name: 'Process Tool',
    icon: '⚗',
    description: 'Fab process module — set Process Type for label color',
    defaultDimensions: { width: 11, height: 10.5 },
    category: 'Equipment',
    defaultProperties: {
      objectType: 'equipment',
      equipmentModel: 'Process Tool',
      processType: 'PHOTO',
      portCount: 2,
      source: 'toolbox',
    },
  },
  {
    id: 'line',
    type: 'rectangle',
    name: 'Line',
    icon: '─',
    description: 'Straight line annotation',
    defaultDimensions: { width: 120, height: 4 },
    category: 'Annotations',
    defaultProperties: { annotationShape: 'line' },
  },
  {
    id: 'area-rect',
    type: 'rectangle',
    name: 'Rectangle',
    icon: '▭',
    description: 'Rectangular area annotation',
    defaultDimensions: DEFAULT_DIMENSIONS.rectangle,
    category: 'Annotations',
  },
  {
    id: 'zone-circle',
    type: 'circle',
    name: 'Circle',
    icon: '○',
    description: 'Circular zone annotation',
    defaultDimensions: DEFAULT_DIMENSIONS.circle,
    category: 'Annotations',
  },
  {
    id: 'label-text',
    type: 'text',
    name: 'Label',
    icon: 'T',
    description: 'Text label annotation',
    defaultDimensions: DEFAULT_DIMENSIONS.text,
    category: 'Annotations',
  },
];

/**
 * Display labels for component classes (used in the Properties panel).
 */
export const CLASS_LABELS: Record<ElementType, string> = {
  'overhead-rail': 'Overhead Rail',
  'overhead-curve': 'Overhead Curve',
  'cross-rail': 'Cross Rail Junction',
  'oht-vehicle': 'OHT Vehicle',
  stocker: 'Stocker',
  stb: 'Side Track Buffer',
  ohb: 'Overhead Hoist Buffer',
  lifter: 'Lifter',
  'support-pillar': 'Support Pillar',
  'horizontal-beam': 'Horizontal Beam',
  'suspension-rod': 'Suspension Rod',
  rectangle: 'Area',
  circle: 'Zone',
  text: 'Label',
};

/**
 * Editable parametric fields per component class (F2.2–F2.4 parameters such as
 * rail direction/height, stocker bay sizing, buffer slot counts).
 */
export const COMPONENT_SCHEMAS: Partial<Record<ElementType, ComponentField[]>> = {
  'overhead-rail': [
    { key: 'direction', label: 'Direction', type: 'select', options: ['bi', 'uni'] },
    { key: 'lengthMeters', label: 'Rail Length', type: 'number', unit: 'm' },
  ],
  'overhead-curve': [
    { key: 'direction', label: 'Direction', type: 'select', options: ['bi', 'uni'] },
  ],
  'cross-rail': [
    { key: 'direction', label: 'Direction', type: 'select', options: ['bi', 'uni'] },
    { key: 'junctionWaitSec', label: 'Junction Wait', type: 'number', unit: 's' },
  ],
  'oht-vehicle': [
    // Design-time optional override only. Empty = backend default profile.
    { key: 'cruiseSpeedOverride', label: 'Cruise Speed', type: 'number', unit: 'm/s' },
  ],
  stocker: [
    // { key: 'showLabel', label: 'Show Label', type: 'boolean' },
  ],
  stb: [{ key: 'slots', label: 'Slots', type: 'number' }],
  ohb: [
    { key: 'slots', label: 'Slots', type: 'number' },
    { key: 'mount', label: 'Mount', type: 'select', options: ['inline', 'spur'] },
  ],
  lifter: [
    {
      key: 'mode',
      label: 'Mode',
      type: 'select',
      options: ['manual', 'auto-interbay', 'vertical-transfer'],
    },
  ],
};

/**
 * Keyboard shortcuts
 */
export const KEYBOARD_SHORTCUTS = {
  UNDO: ['ctrl+z', 'cmd+z'],
  REDO: ['ctrl+shift+z', 'ctrl+y', 'cmd+shift+z', 'cmd+y'],
  DELETE: ['delete', 'backspace'],
  DUPLICATE: ['ctrl+d', 'cmd+d'],
  SELECT_ALL: ['ctrl+a', 'cmd+a'],
  COPY: ['ctrl+c', 'cmd+c'],
  PASTE: ['ctrl+v', 'cmd+v'],
  DESELECT: ['escape'],
  SAVE: ['ctrl+s', 'cmd+s'],
  ZOOM_IN: ['ctrl+=', 'cmd+='],
  ZOOM_OUT: ['ctrl+-', 'cmd+-'],
  FIT_TO_SCREEN: ['ctrl+0', 'cmd+0'],
  TOGGLE_GRID: ['ctrl+g', 'cmd+g'],
};

/**
 * Application version for serialization
 */
export const APP_VERSION = '1.0.0';

/**
 * Local storage keys
 */
export const STORAGE_KEYS = {
  PROJECT: 'layout_editor_project',
  AUTOSAVE: 'layout_editor_autosave',
  SETTINGS: 'layout_editor_settings',
  THEME: 'layout_editor_theme',
};

/**
 * Color-coding by component class (parametric geometry tinting).
 * 2D tracks stay red; 3D DualRailTrack uses its own silver palette.
 */
export const ELEMENT_COLORS: Record<ElementType, string> = {
  'overhead-rail': '#dc2626', // red rail (2D)
  'overhead-curve': '#dc2626', // red rail (2D)
  'cross-rail': '#b91c1c', // darker red junction (2D)
  'oht-vehicle': '#F1F0EB', // warm white OHT capsule body
  stocker: '#CFC8BC', // warm light gray stocker frame
  stb: '#F1F0EB', // warm white EFEM
  ohb: '#F1F0EB', // warm white buffer
  lifter: '#E5E7E8', // light neutral lifter
  'support-pillar': '#B8BDC1',
  'horizontal-beam': '#B8BDC1',
  'suspension-rod': '#A7B0B8',
  rectangle: '#F1F0EB', // equipment footprint
  circle: '#E5E7E8',
  text: '#293A4C',
};
